import { EnquiryCta } from "@/components/enquiry-cta";
import { Link } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";


import { FIRM, PEOPLE } from "@/lib/firm";

export function PageHero({
  eyebrow,
  title,
  lead,
  children,
  cta,
  personSlug,
  image,
}: {
  eyebrow?: string;
  title: string;
  lead: string;
  children?: ReactNode;
  cta?: string;
  /** Show this solicitor's photo prominently beside the heading. */
  personSlug?: string | undefined;
  /** Full-width coastal banner photo behind the heading. */
  image?: { src: string; alt: string };
}) {
  const person = personSlug ? PEOPLE.find((p) => p.slug === personSlug) : undefined;
  if (!person && image) {
    return (
      <section className="relative isolate overflow-hidden border-b border-border">
        <img
          src={image.src}
          alt={image.alt}
          width={1920}
          height={768}
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-teal/90 via-brand-teal/60 to-transparent" />
        <div className="absolute inset-0 bg-brand-teal/40 sm:hidden" />
        <div className="relative mx-auto max-w-6xl px-5 py-16 sm:py-24">
          <div className="max-w-2xl">
            {eyebrow && (
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground/80">
                {eyebrow}
              </p>
            )}
            <h1 className="mt-3 font-serif text-4xl leading-tight text-primary-foreground sm:text-5xl">
              {title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-primary-foreground/90">{lead}</p>
            {cta && <EnquiryCta label={cta} />}
            {children}
          </div>
        </div>
      </section>
    );
  }
  if (!person) {
    return (
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-3xl px-5 py-16 sm:py-20">
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-foreground/70">
              {eyebrow}
            </p>
          )}
          <h1 className="mt-3 font-serif text-4xl leading-tight text-foreground sm:text-5xl">
            {title}
          </h1>
          <p className="measure mt-5 text-lg leading-relaxed text-muted-foreground">{lead}</p>
          {cta && <EnquiryCta label={cta} />}
          {children}
        </div>
      </section>
    );
  }
  return (
    <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-secondary/70 via-background to-secondary/40">
      <div className="mx-auto grid max-w-6xl items-center gap-8 px-5 py-8 sm:py-10 lg:grid-cols-[1.6fr_1fr]">
        <div>
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-foreground/70">
              {eyebrow}
            </p>
          )}
          <h1 className="mt-3 font-serif text-4xl leading-tight text-foreground sm:text-5xl">
            {title}
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">{lead}</p>
          <p className="mt-5 flex items-center gap-2 text-sm text-foreground">
            <span className="text-primary" aria-hidden>★★★★★</span>
            <span className="font-medium">5.0 on Google</span>
            <span className="text-muted-foreground">· local clients, real reviews</span>
          </p>
          {cta && <EnquiryCta label={cta} />}
          {children}
        </div>
        <figure className="relative mx-auto w-full max-w-[200px] sm:max-w-[240px]">
          <div className="absolute -inset-3 -z-0 rounded-md bg-primary/15 rotate-2" aria-hidden />
          <img
            src={person.photo}
            alt={`${person.name}, ${person.role} at ${FIRM.name}`}
            className="relative aspect-[4/5] w-full rounded-md object-cover object-top shadow-xl"
            loading="eager"
          />
          <figcaption className="relative -mt-10 ml-auto mr-4 w-fit rounded-sm border border-border bg-card px-4 py-3 shadow-lg">
            <p className="font-serif text-lg leading-tight text-foreground">{person.name}</p>
            <p className="text-xs text-muted-foreground">
              {person.role} · {person.based}
            </p>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

export function CtaBand({
  title = "Talk to a local lawyer today",
  body = "Call us, or send an enquiry and we'll come back to you with a fixed fee and clear next steps.",
}: {
  title?: string;
  body?: string;
}) {
  return (
    <section className="mx-auto mt-20 max-w-6xl px-5">
      <div className="rounded-sm bg-primary px-6 py-12 text-center sm:px-12">
        <h2 className="font-serif text-3xl text-primary-foreground">{title}</h2>
        <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-primary-foreground/80">
          {body}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={FIRM.phoneHref}
            className="inline-flex items-center justify-center rounded-sm bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground transition-opacity hover:opacity-90"
          >
            Call {FIRM.phone}
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-sm border border-primary-foreground/30 px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-foreground/10"
          >
            Send an enquiry
          </Link>
        </div>
      </div>
    </section>
  );
}

export function FaqList({ items }: { items: ReadonlyArray<{ q: string; a: string }> }) {
  return (
    <div className="divide-y divide-border border-y border-border">
      {items.map((item) => (
        <details key={item.q} className="group py-1">
          <summary className="flex cursor-pointer list-none items-start justify-between gap-4 py-5 [&::-webkit-details-marker]:hidden">
            <h3 className="font-serif text-xl leading-snug text-foreground">{item.q}</h3>
            <ChevronDown
              className="mt-1 size-5 shrink-0 text-accent transition-transform group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>
          <p className="measure pb-6 pt-1 text-base leading-7 md:text-lg md:leading-8 text-muted-foreground">{item.a}</p>
        </details>
      ))}
    </div>
  );
}


export function Prose({ children }: { children: ReactNode }) {
  return <div className="prose-legal max-w-none">{children}</div>;
}

/** Photo strip of the lawyers who run a given practice area. */
export function PeopleStrip({
  slugs,
  heading = "Who you will work with",
}: {
  slugs: ReadonlyArray<string>;
  heading?: string;
}) {
  const people = PEOPLE.filter((p) => slugs.includes(p.slug));
  return (
    <div className="mt-16">
      <h2 className="font-serif text-3xl text-foreground">{heading}</h2>
      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        {people.map((p) => (
          <div key={p.slug} className="flex gap-4 rounded-sm border border-border bg-card p-5">
            <img
              src={p.photo}
              alt={`${p.name}, ${p.role} at ${FIRM.name}`}
              loading="lazy"
              width={160}
              height={160}
              className="h-32 w-32 shrink-0 rounded-sm border border-border object-cover object-top sm:h-40 sm:w-40"
            />
            <div>
              <p className="font-serif text-xl text-foreground">{p.name}</p>
              <p className="text-sm text-muted-foreground">{p.role}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Admitted {p.admitted}. Based in {p.based}.
              </p>
              <Link to="/our-people" className="mt-2 inline-block text-sm font-semibold text-primary underline">
                Read {p.name.split(" ")[0]}'s profile
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
